/** Certificates: record one from its document, and reprice - ADR-0226 (decision D-04).
 *
 * Four calls and nothing computed here: the list (with the certificate documents still waiting to be
 * recorded and what the extraction read from them), the preview ("what will move"), the record and the
 * reprice of one already recorded. Every sentence a refusal shows is the server's (design rule 7).
 *
 * **A write invalidates everything**, as an apply and a manual entry do: a repriced year moves the
 * Scope 2 totals, the market column, the receipts' currency, the evidence claim on the home screen and
 * the filing verdict, and naming the keys would be a list to keep in step with every screen added later.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type Instruments = components["schemas"]["InstrumentListOut"];
export type InstrumentRow = components["schemas"]["InstrumentOut"];
export type CertificateDocument = components["schemas"]["CertificateDocumentOut"];
export type InstrumentInput = components["schemas"]["InstrumentRecordIn"];
export type InstrumentPreview = components["schemas"]["InstrumentPreviewOut"];
export type InstrumentRecorded = components["schemas"]["InstrumentRecordedOut"];
export type InstrumentRepriced = components["schemas"]["InstrumentRepricedOut"];

export const instrumentKeys = { all: ["instruments"] as const };

/** The certificates held and the documents waiting. Read only while the drawer is open: nothing on the
 * Scope 2 page needs it, so a page that never opens the drawer never asks. */
export function useInstruments(enabled: boolean) {
  return useQuery({
    queryKey: instrumentKeys.all,
    enabled,
    retry: false,
    queryFn: async (): Promise<Instruments> => {
      const { data, error, response } = await api.GET("/api/v1/instruments", {});
      // Thrown: a read that cannot fail would draw a workspace with no certificates for a failed read.
      if (error || !data) throw refusalFrom(response, error, "The certificates could not be read.");
      return data;
    },
  });
}

/** What recording would reprice, measured by the server running it and rolling it back. */
export function usePreviewInstrument() {
  return useMutation({
    mutationFn: async (input: InstrumentInput): Promise<InstrumentPreview> => {
      const { data, error, response } = await api.POST("/api/v1/instruments/preview", {
        body: input,
      });
      if (error || !data) throw refusalFrom(response, error, "That could not be checked.");
      return data;
    },
  });
}

export function useRecordInstrument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: InstrumentInput): Promise<InstrumentRecorded> => {
      const { data, error, response } = await api.POST("/api/v1/instruments", { body: input });
      if (error || !data) throw refusalFrom(response, error, "That could not be recorded.");
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

export function useRepriceInstrument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      postCloseReason?: string;
    }): Promise<InstrumentRepriced> => {
      const { data, error, response } = await api.POST("/api/v1/instruments/{instrument_id}/reprice", {
        params: { path: { instrument_id: input.id } },
        body: input.postCloseReason ? { post_close_reason: input.postCloseReason } : {},
      });
      if (error || !data) throw refusalFrom(response, error, "That could not be repriced.");
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries(),
  });
}
